import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

import styles from '../../../styles/component.module.scss';
import integrationAppStyles from './style.module.scss';
import PageHeader from '../../Header/PageHeader';
import AppCard from '../../AppCard';
import AppModal from './AppModal';
import ConfirmationModal from '../../Modal/ConfirmationModal';
import CustomButton from '../../Button/customButton/CustomButton';
import { fetchAppList, deleteApps } from '../../../utils/api/app_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { useSnackbar } from '../../../utils/useSnackbar';
import { setActivePage } from '../../../features/navigation/navigationSlice';

const IntegrationApp = () => {
  const dispatch = useDispatch();
  const activePage = useSelector((state) => state.navigation.activePage);
  const { showSnackbar } = useSnackbar();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    dispatch(setActivePage('Apps'));
    fetchApps();
  }, [dispatch]);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const response = await fetchAppList();

      if (response.status === STATUS.SUCCESS) {
        setApps(response.data || []);
      } else {
        showSnackbar(response.error || 'Failed to fetch apps', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch apps:', error);
      showSnackbar('Failed to fetch apps', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedApp(null);
    setIsModalOpen(true);
  };

  const handleEdit = (app) => {
    setIsEdit(true);
    setSelectedApp(app);
    setIsModalOpen(true);
  };

  const handleDelete = (app) => {
    setSelectedApp(app);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteApps({ appIds: [selectedApp.id] });

      if (response.status === STATUS.SUCCESS) {
        setIsDeleteModalOpen(false);
        setSelectedApp(null);
        showSnackbar(response.message || 'App deleted successfully', 'success');
        fetchApps();
      } else {
        showSnackbar(response.error || 'Failed to delete app', 'error');
      }
    } catch (error) {
      console.error('Error deleting app:', error);
      showSnackbar('Failed to delete app', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSuccess = () => {
    fetchApps();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedApp(null);
    setIsEdit(false);
  };

  const breadcrumbItems = [{ label: 'Settings' }, { label: activePage }];

  return (
    <Box className={styles['page-container']}>
      <PageHeader
        projectName={null}
        title="Apps"
        description="Manage integration apps"
        activePage="Apps"
        breadcrumbItems={breadcrumbItems}
        actions={[
          <CustomButton
            key="create-app"
            variant="contained"
            startIcon={<PlusIcon className="icon" />}
            onClick={handleCreate}
            disabled={loading}
          >
            Create App
          </CustomButton>
        ]}
      />
      <Box
        className={`${styles['cover-container-col-auto']} ${integrationAppStyles.gridContainer}`}
      >
        <Box className={integrationAppStyles.cardContainer}>
          {loading ? (
            <Box className={integrationAppStyles.loadingState}>Loading apps...</Box>
          ) : apps.length === 0 ? (
            <Box className={integrationAppStyles.emptyState}>No apps found. Create your first app!</Box>
          ) : (
            apps.map((app) => (
              <AppCard
                key={app.id}
                header={app.name}
                description={app.description || ''}
                icon={app.image_url}
                showButton={true}
              >
                <Box className={integrationAppStyles.buttonContainer}>

                  <CustomButton
                    variant="outlined"
                    color="error"
                    startIcon={<TrashIcon className="icon  cursor-pointer" />}
                    onClick={() => handleDelete(app)}
                    disabled={loading}
                  >
                    Delete
                  </CustomButton>
                  <CustomButton
                    variant="contained"
                    startIcon={<PencilIcon className="icon cursor-pointer" />}
                    onClick={() => handleEdit(app)}
                    disabled={loading}
                  >
                    Edit
                  </CustomButton>
                </Box>
              </AppCard>
            ))
          )}
        </Box>
      </Box>

      {/* Create/Edit Modal */}
      <AppModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        isEdit={isEdit}
        initialValues={selectedApp || {}}
        existingApps={apps}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedApp(null);
        }}
        onConfirm={confirmDelete}
        title="Delete App"
        message={`Are you sure you want to delete "${selectedApp?.name}"? This action cannot be undone.`}
      />
    </Box>
  );
};

export default IntegrationApp;